/**
 * Web Capability Policy Hooks (REQ-WEB-007 / ADR-118)
 *
 * Global DR policy of the native `web` capability (web_search + delegated
 * web_fetch): provider selection, hardening defaults and reputation fail mode.
 *
 * API: GET/PUT /system/web-policy (SystemAdmin — Settings → System).
 * The model is tenant-ready (future per-tenant policy) but this surface only
 * edits the global/platform level.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface WebPolicyProviderInfo {
  slug: string;
  kind: 'search' | 'extraction';
  display_name: string;
  processing_region: string | null;
  zdr: boolean | null;
  health: string;
}

export interface WebPolicySettings {
  selection?: {
    search_provider?: string;
    fallback_chain?: string[];
    extraction_provider?: string;
    tenant_allowlist?: string[];
    eu_only?: boolean;
  };
  search?: {
    max_results?: number;
    safe_search?: 'off' | 'moderate' | 'strict';
    freshness?: 'any' | 'day' | 'week' | 'month' | 'year';
    strict_allowlist?: boolean;
    domain_denylist?: string[];
  };
  fetch?: {
    javascript_enabled?: boolean;
    js_allowed_domains?: string[];
    page_timeout_seconds?: number;
    session_ttl_seconds?: number;
    cache_ttl_minutes?: number;
  };
  limits?: {
    caps_per_turn?: number;
    caps_per_day?: number;
    caps_per_tool_turn?: Record<string, number>;
    burst_window_seconds?: number;
  };
  security?: {
    reputation_fail_mode?: 'closed' | 'open_degraded';
    approval_search?: boolean;
    approval_fetch?: boolean;
  };
  [section: string]: unknown;
}

export interface WebPolicy {
  settings: WebPolicySettings;
  updated_by: string | null;
  updated_at: string | null;
  providers: WebPolicyProviderInfo[];
}

// =============================================================================
// Hooks
// =============================================================================

export function useWebPolicy() {
  return useQuery({
    queryKey: ['system', 'web-policy'],
    queryFn: async (): Promise<WebPolicy> => {
      const { data } = await rawApi.get<WebPolicy>('/system/web-policy');
      return data;
    },
  });
}

export function useUpdateWebPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: WebPolicySettings): Promise<WebPolicy> => {
      const { data } = await rawApi.put<WebPolicy>('/system/web-policy', { settings });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['system', 'web-policy'], data);
    },
  });
}
